import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Activity, DollarSign, Download, Package, Search } from 'lucide-react';
import { Button, DataState, Panel, PanelHeader, ProgressBar, SinDatos, Table, Td, Thead, Tr } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { exportarDemanda } from '@/lib/exports';
import { cn } from '@/lib/cn';
import { LostSalesBarChart } from '@/components/charts/LostSalesBarChart';
import { useUnmetDemand } from '@/lib/hooks';
import { formatCompactCop, formatCop, formatNumber } from '@shared/format';
import type { UnmetDemandRecord } from '@shared/types';

// Se quitó la columna "Canal / Ciudad": el registro de faltante del B2B no trae ese dato y la
// columna mostraba el mismo guion en las 183 filas.
const COLUMNS = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'producto', label: 'Producto' },
  { key: 'solicitado', label: 'Solicitado', align: 'right' as const },
  { key: 'entregado', label: 'Entregado', align: 'right' as const },
  { key: 'no_entregado', label: 'No Entregado', align: 'right' as const },
  { key: 'perdido', label: '$ Perdido (est.)', align: 'right' as const },
];

export default function UnmetDemandPage() {
  const unmet = useUnmetDemand();
  const [search, setSearch] = useState('');

  return (
    <DataState query={unmet}>
      {(d) => {
        const maxChannelValue = Math.max(0, ...d.byChannel.map((c) => c.value));
        const maxUnits = Math.max(1, ...d.topProducts.map((p) => p.units));
        const fillRateGap =
          d.fillRate == null ? null : Math.round((d.fillRate - d.fillRateTarget) * 10) / 10;
        // Cumplir el objetivo no es un problema: decide la palabra y el color de la tarjeta.
        const cumple = fillRateGap != null && fillRateGap >= 0;
        const rango = d.rangeFrom && d.rangeTo ? `${d.rangeFrom} al ${d.rangeTo}` : 'sin datos';

        if (d.incidents === 0) {
          return (
            <div className="space-y-6">
              <PageHeader title="Demanda Insatisfecha" subtitle="Pedidos que no se pudieron entregar" />
              <SinDatos
                title="No hay faltantes registrados"
                detail="Acá aparece lo que un cliente pidió y no se le pudo entregar. Es el dato que alimenta el fill rate y las ventas perdidas del Dashboard."
                comoLlenarlo="Sale de los registros de demanda del B2B: apretá «Actualizar datos» en el Dashboard. Si sigue vacío, es que en el período no hubo faltantes."
              />
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <PageHeader
              title="Demanda Insatisfecha"
              subtitle={`Pedidos no atendidos por falta de inventario — ${rango}`}
              actions={
                <Button
                  variant="subtle"
                  icon={<Download className="h-3.5 w-3.5" />}
                  disabled={d.records.length === 0}
                  onClick={() => exportarDemanda(d.records)}
                >
                  Exportar
                </Button>
              }
            />

            {/* Stat cards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <StatCard
                icon={<DollarSign className="h-4 w-4 text-danger-text" />}
                label="Venta Perdida (estimada)"
                value={formatCompactCop(d.lostValueTotal)}
                sub={`COP en el período · ${formatCompactCop(d.lostValue7d)} en los últimos 7 días`}
              />
              <StatCard
                icon={<Package className="h-4 w-4 text-ink-muted" />}
                label="Unidades No Entregadas"
                value={formatNumber(d.unitsNotDelivered)}
                sub={`en ${d.incidents} incidentes · ${d.skusAffected} SKU afectados`}
              />
              {/* ⚠️ Decía "brecha" y se pintaba de ámbar SIEMPRE, mirara lo que mirara: con
                  99,1% contra un objetivo de 97% anunciaba una "brecha de 2.1%" —que en
                  realidad es cuánto lo SUPERA— en el color de un problema. Y al revés,
                  veinte puntos por debajo se habría visto exactamente igual. */}
              <StatCard
                icon={
                  <Activity className={cn('h-4 w-4', cumple ? 'text-success-text' : 'text-warning-text')} />
                }
                label="Fill Rate"
                value={d.fillRate == null ? '—' : `${d.fillRate}%`}
                valueClassName={cumple ? 'text-success-text' : 'text-warning-text'}
                sub={
                  d.fillRate == null
                    ? 'sin datos suficientes'
                    : cumple
                      ? `objetivo: ${d.fillRateTarget}% — lo supera por ${fillRateGap}% · por unidades`
                      : `objetivo: ${d.fillRateTarget}% — faltan ${Math.abs(fillRateGap ?? 0)}% · por unidades`
                }
              />
            </div>

            {/* El importe es estimado y hay que decirlo: esa venta no ocurrió, no se facturó. */}
            <p className="text-2xs leading-relaxed text-ink-faint">
              La venta perdida se estima valorizando cada unidad faltante al precio promedio al que
              ese mismo SKU se vendió en el período. No es un importe facturado. Los SKU que nunca
              se vendieron (porque nunca hubo stock) no tienen precio de referencia y suman 0.
            </p>

            {/* Two-column row */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Panel>
                <PanelHeader title="Ventas Perdidas — Tendencia Mensual" />
                <div className="p-5">
                  <LostSalesBarChart data={d.monthlyTrend} />
                </div>
              </Panel>

              <Panel>
                <PanelHeader title="Productos más afectados" subtitle="Por unidades que no se pudieron entregar" />
                <div className="space-y-5 p-5">
                  {/* El desglose por canal no está: el B2B no guarda el canal en el faltante. */}
                  {d.byChannel.length > 0 && (
                    <div className="space-y-5 border-b border-hairline pb-5">
                      {d.byChannel.map((c) => (
                        <div key={c.channel}>
                          <div className="mb-1.5 flex items-center justify-between gap-3">
                            <span className="text-sm text-ink-strong">{c.label}</span>
                            <span className="text-sm font-semibold text-ink-strong">{formatCop(c.value)}</span>
                          </div>
                          <ProgressBar value={c.value} max={maxChannelValue} tone="danger" />
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="space-y-4">
                    {d.topProducts.map((p) => (
                      <div key={p.sku}>
                        <div className="mb-1.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-medium text-ink-strong">{p.productName}</p>
                            <p className="text-2xs text-ink-faint">
                              <span className="font-mono">{p.sku}</span> • {p.incidents}{' '}
                              {p.incidents === 1 ? 'incidente' : 'incidentes'}
                              {p.lostValue > 0 && <> • {formatCop(p.lostValue)} est.</>}
                            </p>
                          </div>
                          <span className="shrink-0 text-sm font-semibold text-warning-text">
                            {formatNumber(p.units)} uds
                          </span>
                        </div>
                        <ProgressBar value={p.units} max={maxUnits} tone="warning" />
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>
            </div>

            {/* Detailed records */}
            <Panel>
              <PanelHeader
                title="Registros Detallados"
                right={
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Filtrar…"
                      className="h-9 w-56 rounded-xl border border-hairline bg-panel pl-9 pr-3 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none"
                    />
                  </div>
                }
              />
              <RecordsTable records={d.records} search={search} />
            </Panel>
          </div>
        );
      }}
    </DataState>
  );
}

function RecordsTable({ records, search }: { records: UnmetDemandRecord[]; search: string }) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter((r) => `${r.sku} ${r.productName} ${r.date}`.toLowerCase().includes(q));
  }, [records, search]);

  return (
    <Table>
      <Thead columns={COLUMNS} />
      <tbody>
        {filtered.map((r) => (
          <Tr key={r.id}>
            <Td><span className="text-xs text-ink-muted">{r.date}</span></Td>
            <Td>
              <p className="font-medium text-ink-strong">{r.productName}</p>
              <p className="font-mono text-2xs text-ink-faint">{r.sku}</p>
            </Td>
            <Td align="right"><span className="text-ink">{formatNumber(r.requested)}</span></Td>
            <Td align="right"><span className="text-ink">{formatNumber(r.delivered)}</span></Td>
            <Td align="right"><span className="font-semibold text-warning-text">{formatNumber(r.notDelivered)}</span></Td>
            <Td align="right">
              {r.lostValue > 0 ? (
                <span className="font-semibold text-danger-text">{formatCop(-r.lostValue)}</span>
              ) : (
                <span className="font-mono text-2xs text-ink-faint" title="Ese SKU no tiene venta en el período: no hay precio de referencia">
                  sin precio
                </span>
              )}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

function StatCard({
  icon,
  label,
  value,
  sub,
  valueClassName,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  sub: string;
  valueClassName?: string;
}) {
  return (
    <div className="surface p-5">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs font-medium text-ink-muted">{label}</span>
      </div>
      <p className={`mt-3 text-3xl font-bold ${valueClassName ?? 'text-ink-strong'}`}>{value}</p>
      <p className="mt-1.5 text-2xs text-ink-faint">{sub}</p>
    </div>
  );
}
